import { createLogic } from 'redux-logic';
import { stopSubmit, setSubmitFailed, reset } from 'redux-form';
import {
  GET_SHIPPING_ADDRESSES_PENDING, ADD_SHIPPING_ADDRESS_PENDING, PATH_SHIPPING_ADDRESSES,
  DELETE_SHIPPING_ADDRESS_CONFIRM, DELETE_SHIPPING_ADDRESS_PENDING, UPDATE_SHIPPING_ADDRESS_PENDING
} from '../constants';
import {
  getShippingAddressesSuccess, getShippingAddressesError, addShippingAddressSuccess, addShippingAddressError,
  toggleForm, toggleModal, triggerModal, toggleProcessedItem, deleteShippingAddressSuccess,
  deleteShippingAddressError, deleteShippingAddressPending, updateShippingAddressSuccess,
  updateShippingAddressError, setBannerState, setAddressSuggestions, getShippingAddressesPending
} from '../actions/AppActions';
import { getShippingAddressPayload } from '../utils/addressHelpers';
import history from '../utils/history';

export const getShippingAddressesLogic = createLogic({
  type: GET_SHIPPING_ADDRESSES_PENDING,
  latest: true,
  process({ httpClient, getState }, dispatch, done) {
    httpClient(dispatch).get('/shipping-addresses')
      .then(res => {
        dispatch(getShippingAddressesSuccess(res.data));
        done();
      })
      .catch(error => {
        dispatch(setBannerState({ data: { type: 'error', message: getState()?.intl?.messages?.Errors?.Profile?.Get_Shipping_Addresses_Error } }));
        dispatch(getShippingAddressesError({ err: error }));
        done();
      });
  }
});

export const addShippingAddressLogic = createLogic({
  type: ADD_SHIPPING_ADDRESS_PENDING,
  latest: true,
  process({ httpClient, action, getState }, dispatch, done) {
    const form = action?.payload?.form;
    dispatch(setAddressSuggestions());
    httpClient(dispatch).post('/shipping-addresses', getShippingAddressPayload(action?.payload))
      .then(res => {
        dispatch(addShippingAddressSuccess(res.data));
        dispatch(stopSubmit(form));
        dispatch(reset(form));
        history.push(PATH_SHIPPING_ADDRESSES);
        done();
      })
      .catch(error => {
        const addressSuggestions = error?.response?.data?.addressSuggestions;
        const isAddressSuggestionError = error?.response?.status === 406 && addressSuggestions?.length;

        if (isAddressSuggestionError) {
          dispatch(setAddressSuggestions(addressSuggestions));
        }

        const intlErrors = getState()?.intl?.messages?.Errors;
        const errorMessage = isAddressSuggestionError
          ? intlErrors?.ShippingAddresses?.Address_Suggestion_Error
          : intlErrors?.ShippingAddresses?.Add_Address_Error;

        dispatch(addShippingAddressError({ err: error?.response?.data ?? error, status: error?.response?.status }));
        dispatch(stopSubmit(form, { _error: errorMessage }));
        dispatch(setSubmitFailed(form));
        done();
      });
  }
});

export const updateShippingAddressLogic = createLogic({
  type: UPDATE_SHIPPING_ADDRESS_PENDING,
  latest: true,
  process({ httpClient, action, getState }, dispatch, done) {
    const form = action?.payload?.form;
    const data = getShippingAddressPayload(action.payload);
    dispatch(setAddressSuggestions());
    httpClient(dispatch).put('/shipping-addresses', data)
      .then(res => {
        dispatch(stopSubmit(form));
        dispatch(updateShippingAddressSuccess({ existingId: data.id, updatedShippingAddress: res.data }));
        history.push(PATH_SHIPPING_ADDRESSES);
        dispatch(reset(form));
        done();
      })
      .catch(error => {
        const addressSuggestions = error?.response?.data?.addressSuggestions;
        const isAddressSuggestionError = error?.response?.status === 406 && addressSuggestions?.length;

        if (isAddressSuggestionError) {
          dispatch(setAddressSuggestions(addressSuggestions));
        }

        const intlErrors = getState()?.intl?.messages?.Errors;
        const errorMessage = isAddressSuggestionError
          ? intlErrors?.ShippingAddresses?.Address_Suggestion_Error
          : intlErrors?.ShippingAddresses?.Update_Address_Error;

        dispatch(updateShippingAddressError({ err: error?.response?.data ?? error, status: error?.response?.status }));
        dispatch(stopSubmit(form, { _error: errorMessage }));
        dispatch(setSubmitFailed(form));
        done();
      });
  }
});

export const deleteShippingAddressPendingLogic = createLogic({
  type: DELETE_SHIPPING_ADDRESS_PENDING,
  latest: true,
  process({ httpClient, action }, dispatch, done) {
    const shippingAddressId = action?.payload;
    httpClient(dispatch).delete(`/shipping-addresses/${shippingAddressId}`)
      .then(() => {
        dispatch(deleteShippingAddressSuccess(shippingAddressId));
        history.push(PATH_SHIPPING_ADDRESSES);
        done();
      })
      .catch(error => {
        dispatch(deleteShippingAddressError(error));
        done();
      });
  }
});

export const deleteShippingAddressConfirmLogic = createLogic({
  type: DELETE_SHIPPING_ADDRESS_CONFIRM,
  latest: true,
  processOptions: {
    dispatchMultiple: true,
  },
  process({ action }, dispatch) {
    const modalProps = {
      show: true,
      message: `Are you sure you want to delete the following address:
                ${action.payload.line1}?`,
      okLabel: 'Yes',
      okDataHook: 'deleteAddressYes.button',
      cancelLabel: 'No',
      cancelDataHook: 'deleteAddressNo.button',
      buttonOrder: 'rtl',
      okClick: () => {
        dispatch(toggleProcessedItem(action.payload.id));
        dispatch(deleteShippingAddressPending(action.payload.id));
        dispatch(toggleModal());
      },
      cancelClick: () => {
        history.push(PATH_SHIPPING_ADDRESSES);
        dispatch(toggleModal());
      }
    };
    dispatch(toggleForm(null));
    dispatch(triggerModal(modalProps));
  }
});

export default [
  getShippingAddressesLogic,
  addShippingAddressLogic,
  updateShippingAddressLogic,
  deleteShippingAddressConfirmLogic,
  deleteShippingAddressPendingLogic
];
